import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Phone, Mail, Users, CheckCircle2, Clock, AlertCircle, Search, Plus, Bell } from "lucide-react";
import { format, isPast, isToday, isTomorrow, parseISO, differenceInDays } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";
import FollowUpForm from "@/components/followups/FollowUpForm";
import ActivityForm from "@/components/shared/ActivityForm";
import { toast } from "sonner";

const typeIcons = {
  call: Phone,
  meeting: Users,
  email: Mail,
  check_in: CheckCircle2,
  review: AlertCircle,
};

const typeLabels = {
  call: "Telefonsamtale",
  meeting: "Møte",
  email: "E-post",
  check_in: "Check-in",
  review: "Gjennomgang",
};

const statusColors = {
  scheduled: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-700",
  missed: "bg-rose-100 text-rose-700",
};

const priorityColors = {
  low: "border-slate-200",
  medium: "border-amber-300",
  high: "border-orange-400",
  urgent: "border-red-500",
};

export default function FollowUps() {
  const [showForm, setShowForm] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("scheduled");
  const [typeFilter, setTypeFilter] = useState("all");
  
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: followUps = [] } = useQuery({
    queryKey: ["followUps"],
    queryFn: () => base44.entities.FollowUp.list("-scheduled_date"),
  });

  const { data: investors = [] } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.FollowUp.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followUps"] });
      setShowForm(false);
      toast.success("Oppfølging opprettet");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.FollowUp.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followUps"] });
      setShowForm(false);
      setEditingFollowUp(null);
      toast.success("Oppfølging oppdatert");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.FollowUp.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followUps"] });
      toast.success("Oppfølging slettet");
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: async (followUp) => {
      await base44.integrations.Core.SendEmail({
        to: followUp.assigned_to,
        subject: `Påminnelse: ${followUp.title}`,
        body: `Hei ${followUp.assigned_to_name},\n\nDette er en påminnelse om planlagt oppfølging:\n\nInvestor: ${followUp.investor_name}\nType: ${typeLabels[followUp.type]}\nTidspunkt: ${format(parseISO(followUp.scheduled_date), "d. MMMM yyyy 'kl.' HH:mm", { locale: nb })}\n\n${followUp.description || ""}\n\nMvh,\nPP-X CRM`,
      });
      await base44.entities.FollowUp.update(followUp.id, { reminder_sent: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["followUps"] });
      toast.success("Påminnelse sendt");
    },
  });

  const handleSubmit = (data) => {
    if (editingFollowUp) {
      updateMutation.mutate({ id: editingFollowUp.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleComplete = (followUp) => {
    updateMutation.mutate({
      id: followUp.id,
      data: {
        ...followUp,
        status: "completed",
        completed_date: new Date().toISOString(),
      }
    });
    setSelectedInvestor(investors.find(i => i.id === followUp.investor_id));
    setShowActivityForm(true);
  };

  const handleActivitySubmit = (activityData) => {
    base44.entities.Activity.create(activityData).then(() => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setShowActivityForm(false);
      setSelectedInvestor(null);
      toast.success("Aktivitet logget");
    });
  };

  const filteredFollowUps = followUps.filter(followUp => {
    const matchesSearch = 
      followUp.title?.toLowerCase().includes(search.toLowerCase()) ||
      followUp.investor_name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || followUp.status === statusFilter;
    const matchesType = typeFilter === "all" || followUp.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const upcomingFollowUps = filteredFollowUps.filter(f => f.status === "scheduled" && !isPast(parseISO(f.scheduled_date)));
  const overdueFollowUps = filteredFollowUps.filter(f => f.status === "scheduled" && isPast(parseISO(f.scheduled_date)));

  const getDateLabel = (date) => {
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) return "I dag";
    if (isTomorrow(parsedDate)) return "I morgen";
    const days = differenceInDays(parsedDate, new Date());
    if (days > 0 && days <= 7) return `Om ${days} dager`;
    return format(parsedDate, "d. MMM yyyy", { locale: nb });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Oppfølginger</h1>
            <p className="text-slate-500 mt-1">{upcomingFollowUps.length} planlagte oppfølginger</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> Ny Oppfølging
          </Button>
        </div>

        {/* Alerts for overdue */}
        {overdueFollowUps.length > 0 && (
          <Card className="p-4 mb-6 bg-red-50 border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">
                  {overdueFollowUps.length} forfalt{overdueFollowUps.length > 1 ? "e" : ""} oppfølging{overdueFollowUps.length > 1 ? "er" : ""}
                </h3>
                <p className="text-sm text-red-700">Disse oppfølgingene har passert planlagt tidspunkt</p>
              </div>
            </div>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Søk oppfølginger..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 bg-white border-0 shadow-sm"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Statuser</SelectItem>
              <SelectItem value="scheduled">Planlagt</SelectItem>
              <SelectItem value="completed">Fullført</SelectItem>
              <SelectItem value="cancelled">Kansellert</SelectItem>
              <SelectItem value="missed">Mistet</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-0 shadow-sm">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Typer</SelectItem>
              <SelectItem value="call">Samtale</SelectItem>
              <SelectItem value="meeting">Møte</SelectItem>
              <SelectItem value="email">E-post</SelectItem>
              <SelectItem value="check_in">Check-in</SelectItem>
              <SelectItem value="review">Gjennomgang</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Follow-ups List */}
        <div className="space-y-4">
          {filteredFollowUps.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen oppfølginger</h3>
              <p className="text-slate-500 mb-6">Opprett din første oppfølging for å holde kontakten med investorer</p>
              <Button onClick={() => setShowForm(true)} className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" /> Ny Oppfølging
              </Button>
            </div>
          ) : (
            filteredFollowUps.map(followUp => {
              const Icon = typeIcons[followUp.type] || Calendar;
              const isOverdue = followUp.status === "scheduled" && isPast(parseISO(followUp.scheduled_date));
              
              return (
                <Card
                  key={followUp.id}
                  className={cn(
                    "p-5 hover:shadow-md transition-all border-l-4",
                    priorityColors[followUp.priority],
                    isOverdue && "bg-red-50"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={cn(
                        "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
                        followUp.status === "completed" ? "bg-emerald-100" :
                        isOverdue ? "bg-red-100" :
                        "bg-lime-100"
                      )}>
                        <Icon className={cn(
                          "w-6 h-6",
                          followUp.status === "completed" ? "text-emerald-600" :
                          isOverdue ? "text-red-600" :
                          "text-zinc-900"
                        )} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <h3 className="font-semibold text-slate-900 text-lg mb-1">{followUp.title}</h3>
                            <p className="text-sm text-slate-600 mb-2">{followUp.investor_name}</p>
                          </div>
                          <Badge className={cn("border-0 shrink-0", statusColors[followUp.status])}>
                            {followUp.status === "scheduled" ? "Planlagt" :
                             followUp.status === "completed" ? "Fullført" :
                             followUp.status === "cancelled" ? "Kansellert" : "Mistet"}
                          </Badge>
                        </div>

                        {followUp.description && (
                          <p className="text-sm text-slate-600 mb-3">{followUp.description}</p>
                        )}

                        <div className="flex flex-wrap gap-3 text-sm">
                          <div className={cn(
                            "flex items-center gap-1",
                            isOverdue ? "text-red-600 font-semibold" : "text-slate-600"
                          )}>
                            <Clock className="w-4 h-4" />
                            <span>{format(parseISO(followUp.scheduled_date), "d. MMM yyyy 'kl.' HH:mm", { locale: nb })}</span>
                            {isOverdue && <span className="ml-1">(Forfalt)</span>}
                          </div>
                          
                          <Badge variant="outline" className="text-xs">
                            {typeLabels[followUp.type]}
                          </Badge>

                          <Badge variant="outline" className={cn(
                            "text-xs",
                            followUp.priority === "urgent" && "border-red-300 text-red-700",
                            followUp.priority === "high" && "border-orange-300 text-orange-700"
                          )}>
                            {followUp.priority === "urgent" ? "Haster" :
                             followUp.priority === "high" ? "Høy" :
                             followUp.priority === "medium" ? "Medium" : "Lav"}
                          </Badge>
                        </div>

                        {followUp.notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs font-medium text-slate-500 mb-1">Notater:</p>
                            <p className="text-sm text-slate-700">{followUp.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 shrink-0">
                      {followUp.status === "scheduled" && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleComplete(followUp)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Fullført
                          </Button>
                          {!followUp.reminder_sent && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendReminderMutation.mutate(followUp)}
                              disabled={sendReminderMutation.isPending}
                            >
                              <Bell className="w-4 h-4 mr-1" />
                              Send påminnelse
                            </Button>
                          )}
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingFollowUp(followUp);
                          setShowForm(true);
                        }}
                      >
                        Rediger
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          if (confirm("Er du sikker på at du vil slette denne oppfølgingen?")) {
                            deleteMutation.mutate(followUp.id);
                          }
                        }}
                      >
                        Slett
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Forms */}
      {showForm && (
        <FollowUpForm
          followUp={editingFollowUp}
          investor={editingFollowUp ? investors.find(i => i.id === editingFollowUp.investor_id) : investors[0]}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingFollowUp(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
          currentUser={user}
        />
      )}

      {showActivityForm && selectedInvestor && (
        <ActivityForm
          relatedTo="investor"
          relatedId={selectedInvestor.id}
          relatedName={selectedInvestor.name}
          onSubmit={handleActivitySubmit}
          onClose={() => {
            setShowActivityForm(false);
            setSelectedInvestor(null);
          }}
          isLoading={false}
        />
      )}
    </div>
  );
}